import { Input } from "@/components/podkit/forms/Input";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import {
    useGetOrganizationPolicies,
    useUpdateOrganizationPolicies,
    type PlainOrganizationPolicies,
} from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";

import type { ChangeEvent, FC, KeyboardEvent } from "react";
import { useCallback, useEffect, useState } from "react";

type PolicyFieldProps = {
    title: string;
    description?: string;
    policyKey: keyof PlainOrganizationPolicies;
    dataTestId?: string;
    editable?: boolean;
};

export const PolicyNumberField: FC<PolicyFieldProps> = ({
    title,
    description,
    policyKey,
    dataTestId,
    editable = true,
}) => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const [initialValue, setInitialValue] = useState<number | "">(10);
    const [value, setValue] = useState<number | "">(10);
    const isDirty = initialValue !== value;

    useEffect(() => {
        if (organizationPolicies && policyKey in organizationPolicies) {
            const policyValue = organizationPolicies[policyKey];
            if (policyValue !== undefined) {
                setValue(Number(policyValue));
                setInitialValue(Number(policyValue));
            }
        }
    }, [organizationPolicies, policyKey]);

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        if (inputValue === "") {
            setValue("");
            return;
        }
        const intValue = parseInt(inputValue, 10);
        if (!isNaN(intValue) && intValue >= 0) {
            setValue(intValue);
        }
    };

    const handleSave = useCallback(async () => {
        if (!isDirty) return;

        const params: PartialMessage<OrganizationPolicies> = {
            [policyKey]: typeof value === "number" ? BigInt(value) : BigInt(0),
        };

        try {
            await updateOrganizationPolicies.mutateAsync(params);
            setInitialValue(value);
            toast({
                title: `${title} updated`,
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
            setValue(initialValue);
        }
    }, [isDirty, policyKey, value, updateOrganizationPolicies, toast, title, initialValue]);

    // Handle save on blur
    const handleBlur = useCallback(() => {
        if (isDirty) {
            void handleSave();
        }
    }, [isDirty, handleSave]);

    // Handle Enter key press
    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && isDirty) {
                void handleSave();
            }
        },
        [isDirty, handleSave],
    );

    return (
        <div className="flex flex-col gap-3 rounded-lg border border-border-subtle px-5 py-4">
            <SkeletonBlock ready={!isLoading}>
                <div className="flex min-h-[3.5rem] flex-col gap-0.5">
                    <Heading3 className="text-base">{title}</Heading3>
                    <Text className="text-sm text-content-secondary">
                        {description}{" "}
                        <ExternalLink
                            href="https://ona.com/docs/ona/organizations/policies/environment-limits"
                            iconSize="sm"
                        >
                            Learn more
                        </ExternalLink>
                    </Text>
                </div>
                <Input
                    data-testid={dataTestId}
                    type="number"
                    value={value}
                    onChange={handleValueChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    min={1}
                    className="w-full"
                    disabled={!editable}
                />
            </SkeletonBlock>
        </div>
    );
};

type MaximumRunningEnvironmentsProps = {
    editable?: boolean;
};

export const MaximumRunningEnvironments: FC<MaximumRunningEnvironmentsProps> = ({ editable = true }) => {
    return (
        <PolicyNumberField
            dataTestId="maximum-running-environments-field"
            title="Maximum concurrent environments"
            description="Maximum running environments per user."
            policyKey="maximumRunningEnvironmentsPerUser"
            editable={editable}
        />
    );
};

type MaximumTotalEnvironmentsProps = {
    editable?: boolean;
};

export const MaximumTotalEnvironments: FC<MaximumTotalEnvironmentsProps> = ({ editable = true }) => {
    return (
        <PolicyNumberField
            dataTestId="maximum-total-environments-field"
            title="Maximum total environments"
            description="Limits total environments (running or stopped) per user. Reducing this limit helps control infrastructure costs."
            policyKey="maximumEnvironmentsPerUser"
            editable={editable}
        />
    );
};
